import ChordSymbol from './ChordSymbol'

const VOICE_KO = { soprano: '소프라노', alto: '알토', tenor: '테너', bass: '베이스' }
const NCT_KO  = { UN: '상행 이웃음', LN: '하행 이웃음', DN: '이중 이웃음', PT: '경과음' }

/**
 * 4분음표 단위로 박마다 반복 표시하지 않고, 실제로 화음이 바뀌는 지점(예: 2분음표면
 * 2박씩) 단위로 묶어서 하나의 행으로 보여준다 — 같은 chordSymbol이 연속되면 한 묶음,
 * NCT(비화성음)는 묶음 안 어느 박에서든 나온 걸 다 모아 보여준다.
 */
function collapseBeatsForDisplay(beats) {
  const groups = []
  for (const b of beats) {
    const last = groups[groups.length - 1]
    if (last && last.chordSymbol === b.chordSymbol) {
      last.beatEnd = b.beat
      last.beats.push(b)
    } else {
      groups.push({ beatStart: b.beat, beatEnd: b.beat, chordSymbol: b.chordSymbol, beats: [b] })
    }
  }
  return groups.map((g) => {
    const first = g.beats[0]
    const nctByVoice = {}
    for (const b of g.beats) {
      if (!b.nct) continue
      for (const voice of ['soprano', 'alto', 'tenor', 'bass']) {
        if (b.nct[voice] && !nctByVoice[voice]) nctByVoice[voice] = b.nct[voice]
      }
    }
    return {
      beatStart: g.beatStart,
      beatEnd: g.beatEnd,
      firstBeatIndex: first.beat,
      chordSymbol: g.chordSymbol,
      inversion: first.inversion,
      analyzedBy: first.analyzedBy,
      advancedAnalysis: g.beats.map((b) => b.advancedAnalysis).filter(Boolean).join(' / ') || null,
      omittedThird: g.beats.some((b) => b.omittedThird),
      nct: Object.keys(nctByVoice).length ? nctByVoice : null,
    }
  })
}

export default function AnalysisPanel({ result, loading, error, appMode = 'analyze', onOverrideChordSymbol }) {
  const grade = result?.grade ?? null
  const titleKind = grade ? '채점' : (appMode === 'solve' && loading ? '채점' : '분석')

  /**
   * 로마숫자 기호를 더블클릭하면 수동으로 고쳐 쓸 수 있게 한다 — 예: 공통화음
   * 전조를 "C: v♭" / "F: ii" 두 줄로 병기. window.prompt는 한 줄 입력이라
   * 사용자는 "\n"(역슬래시+n 두 글자)으로 구분해 입력하고, 저장 시 실제
   * 줄바꿈 문자로 치환한다.
   */
  const handleEditChordSymbol = (measureIndex, beatIndex, current) => {
    if (!onOverrideChordSymbol) return
    const promptDefault = String(current ?? '').replace(/\n/g, '\\n')
    const next = window.prompt(
      '로마숫자 표기 수정 (공통화음 전조 등 두 줄 병기는 "\\n"으로 구분, 예: C: v♭\\nF: ii)',
      promptDefault,
    )
    if (next == null) return
    onOverrideChordSymbol(measureIndex, beatIndex, next.replace(/\\n/g, '\n'))
  }

  return (
    <div className="analysis-panel">
      {loading && (
        <div className="analysis-loading">
          <div className="spinner" />
          <p>{titleKind === '채점' ? '채점 중...' : '분석 중...'}</p>
        </div>
      )}

      {error && (
        <div className="analysis-error">
          <p>오류: {error}</p>
        </div>
      )}

      {result && !loading && (
        <div className="analysis-content">
          {grade && (
            <div className={`grade-banner grade-${grade.score >= 90 ? 'excellent' : grade.score >= 70 ? 'good' : grade.score >= 40 ? 'fair' : 'poor'}`}>
              <div className="grade-score-block">
                <span className="grade-score-num">{grade.score}</span>
                <span className="grade-score-max">/ {grade.maxScore ?? 100}</span>
              </div>
              <div className="grade-summary-block">
                <p className="grade-summary">{grade.summary}</p>
                {grade.issueCount > 0 && (
                  <p className="grade-issue-count">성부진행 지적 {grade.issueCount}건</p>
                )}
              </div>
            </div>
          )}

          {grade?.deductions?.length > 0 && (
            <div className="grade-deductions">
              <strong>감점 내역</strong>
              <ul>
                {grade.deductions.slice(0, 40).map((d, i) => (
                  <li key={i}>
                    <span className="grade-deduct-pts">−{d.points}</span>
                    {d.reason}
                  </li>
                ))}
                {grade.deductions.length > 40 && (
                  <li>… 외 {grade.deductions.length - 40}건</li>
                )}
              </ul>
            </div>
          )}

          <div className="analysis-measures-row">
          {(() => {
            let chordCounter = 0
            return (result.measures ?? []).map((m, idx) => {
            const hasBeats = Array.isArray(m.beats) && m.beats.length > 0
            const rowHasIssues =
              (m.voiceLeadingIssues?.length > 0) ||
              (hasBeats && m.beats.some((b) => b?.voiceLeadingIssues?.length > 0))
            const groups = hasBeats ? collapseBeatsForDisplay(m.beats) : null
            return (
            <div
              key={`${m.measure ?? idx}-${idx}`}
              className={`analysis-measure ${rowHasIssues ? 'has-issues' : ''}`}
            >
              <div className="measure-header">
                <span className="measure-num">마디 {m.measure}</span>
                {m.analyzedBy && (
                  <span className={`analysis-engine-badge engine-${m.analyzedBy}`}>
                    {m.analyzedBy === 'claude' ? 'Claude' : 'Python'}
                  </span>
                )}
                {!hasBeats && (
                  <span className="chord-num">#{++chordCounter}</span>
                )}
                {!hasBeats && (
                  <span
                    className="chord-symbol chord-symbol-editable"
                    title="더블클릭: 로마숫자 표기 수정 (전조 병기 등)"
                    onDoubleClick={() => handleEditChordSymbol(idx, null, m.chordSymbol)}
                  >
                    <ChordSymbol value={m.chordSymbol} />
                  </span>
                )}
              </div>
              <div className="measure-details">
                {m.advancedAnalysis && (
                  <p className="advanced-analysis-measure">{m.advancedAnalysis}</p>
                )}
                {hasBeats ? (
                  <ul className="beat-analysis-list">
                    {groups.map((g) => {
                      chordCounter += 1
                      const num = chordCounter
                      return (
                      <li key={g.firstBeatIndex} className="beat-analysis-row">
                        <span className="chord-num">#{num}</span>
                        <span className="beat-label">박 {g.beatStart + 1}{g.beatEnd > g.beatStart ? `–${g.beatEnd + 1}` : ''}</span>
                        {g.analyzedBy && (
                          <span className={`analysis-engine-badge sm engine-${g.analyzedBy}`}>
                            {g.analyzedBy === 'claude' ? 'C' : 'P'}
                          </span>
                        )}
                        <span
                          className="chord-symbol chord-symbol-editable"
                          title="더블클릭: 로마숫자 표기 수정 (전조 병기 등)"
                          onDoubleClick={() => handleEditChordSymbol(idx, g.firstBeatIndex, g.chordSymbol)}
                        >
                          <ChordSymbol
                            value={g.chordSymbol}
                            em={g.omittedThird}
                            emTitle="3음 생략"
                          />
                        </span>
                        <span className="beat-inversion">{g.inversion ?? '—'}</span>
                        {g.advancedAnalysis && (
                          <span className="beat-advanced">{g.advancedAnalysis}</span>
                        )}
                        {g.nct && Object.values(g.nct).some(Boolean) && (
                          <span className="nct-row">
                            {['soprano','alto','tenor','bass'].map((voice) => {
                              const type = g.nct[voice]
                              if (!type) return null
                              return (
                                <span key={voice} className="nct-item">
                                  <span className="nct-voice">{VOICE_KO[voice]}</span>
                                  <span className="nct-type">{NCT_KO[type] ?? type}</span>
                                </span>
                              )
                            })}
                          </span>
                        )}
                      </li>
                      )
                    })}
                  </ul>
                ) : (
                  <p><strong>전위:</strong> {m.inversion}</p>
                )}
                {m.tonicization && <p><strong>전조/부속:</strong> {m.tonicization}</p>}
                {m.voiceLeadingIssues?.length > 0 && (
                  <div className="issues">
                    <strong>성부 진행 문제:</strong>
                    <ul>
                      {m.voiceLeadingIssues.map((iss, i) => (
                        <li key={i} className="issue-item">⚠ {iss}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
            )
          })
          })()}
          </div>
        </div>
      )}
    </div>
  )
}
